import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";

export const Header = ({ t, lang, onLangChange, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setMenuOpen(false);
        onNavigate(id);
    };

    const links = [
        { href: "/features", label: t.nav.presentation },
        { href: "/nea", label: t.nav.news },
        { id: "pricing", label: t.nav.pricing },
        { id: "download", label: t.nav.download },
        { id: "about", label: t.nav.about },
        { id: "contact", label: t.nav.contact },
        { id: "support", label: t.nav.support },
    ];

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
                scrolled ? "border-white/10 bg-[#032A44]/90 shadow-[0_8px_32px_rgba(0,0,0,0.35)]" : "border-white/5 bg-[#032A44]/70"
            }`}
            data-testid="site-header"
        >
            <div className="mx-auto flex h-[84px] max-w-[1400px] items-center justify-between px-6 md:px-12">
                <button
                    onClick={() => onNavigate("top")}
                    className="flex items-center gap-3.5"
                    data-testid="header-logo"
                    aria-label="TheraOffice home"
                >
                    <img
                        src="/brand/app-icon-512.webp"
                        alt="TheraOffice logo"
                        className="h-12 w-12 rounded-xl object-cover"
                    />
                    <img
                        src="/brand/wordmark-white.webp"
                        alt="TheraOffice"
                        className="h-7 w-auto md:h-8"
                        data-testid="header-wordmark"
                    />
                </button>

                <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
                    {links.map((l) =>
                        l.href ? (
                            <a
                                key={l.href}
                                href={l.href}
                                className="text-sm font-medium text-[#A3B3B8] transition-colors duration-300 hover:text-white"
                                data-testid={`nav-link-${l.href.replace("/", "") || "home"}`}
                            >
                                {l.label}
                            </a>
                        ) : (
                            <button
                                key={l.id}
                                onClick={() => onNavigate(l.id)}
                                className="text-sm font-medium text-[#A3B3B8] transition-colors duration-300 hover:text-white"
                                data-testid={`nav-link-${l.id}`}
                            >
                                {l.label}
                            </button>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setMenuOpen((v) => !v)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#A3B3B8] transition-colors duration-300 hover:border-[#4CC9DC] hover:text-white lg:hidden"
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {menuOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                    <button
                        onClick={() => onLangChange(lang === "el" ? "en" : "el")}
                        className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold tracking-widest text-[#A3B3B8] transition-colors duration-300 hover:border-[#4CC9DC] hover:text-white"
                        data-testid="lang-toggle"
                        aria-label="Switch language"
                    >
                        <Globe size={14} />
                        {lang === "el" ? "EN" : "ΕΛ"}
                    </button>
                    <button
                        onClick={() => onNavigate("download")}
                        className="hidden items-center gap-2 rounded-full bg-[#D97706] px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(217,119,6,0.35)] transition-colors duration-300 hover:bg-[#B45309] sm:flex"
                        data-testid="header-download-button"
                    >
                        {t.nav.cta}
                    </button>
                </div>
            </div>
            <AnimatePresence>
                {menuOpen && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-white/10 bg-[#032A44]/95 backdrop-blur-xl lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex flex-col gap-1 px-6 py-4">
                            {links.map((l) =>
                                l.href ? (
                                    <a
                                        key={l.href}
                                        href={l.href}
                                        className="rounded-xl px-4 py-3 text-left text-base font-semibold text-[#A3B3B8] transition-colors duration-300 hover:bg-white/5 hover:text-white"
                                        data-testid={`mobile-nav-link-${l.href.replace("/", "")}`}
                                    >
                                        {l.label}
                                    </a>
                                ) : (
                                    <button
                                        key={l.id}
                                        onClick={() => go(l.id)}
                                        className="rounded-xl px-4 py-3 text-left text-base font-semibold text-[#A3B3B8] transition-colors duration-300 hover:bg-white/5 hover:text-white"
                                        data-testid={`mobile-nav-link-${l.id}`}
                                    >
                                        {l.label}
                                    </button>
                                )
                            )}
                            <button
                                onClick={() => go("download")}
                                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#D97706] px-5 py-3 text-sm font-bold text-white"
                                data-testid="mobile-download-button"
                            >
                                {t.nav.cta}
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
